import { useState, useEffect, useRef } from 'react'
import { productSchema } from '../../lib/validation'
import type { Product, MediaItem } from '../../types'
import { getProductMedia } from '../../types'
import { useMediaUpload } from '../../hooks/useMediaUpload'
import FormFields from './FormFields'
import MediaUploader from './MediaUploader'

type FormData = Omit<Product, 'id' | 'images' | 'media' | 'createdAt' | 'updatedAt'>

interface Props {
  initial?: Partial<Product>
  onSubmit: (data: FormData & { images: string[]; media: MediaItem[] }) => Promise<void> | void
  onCancel: () => void
  submitLabel: string
}

const defaultForm: FormData = {
  name: '',
  description: '',
  price: 0,
  unit: 'kg',
  quantity: 0,
  status: 'active',
  category_id: null,
  custom_sku: null, 
}

export default function ProductForm({ initial, onSubmit, onCancel, submitLabel }: Props) {
  const [form, setForm] = useState<FormData>({
    ...defaultForm,
    ...(initial ? {
      name:        initial.name        ?? defaultForm.name,
      description: initial.description ?? defaultForm.description,
      price:       initial.price       ?? defaultForm.price,
      unit:        initial.unit        ?? defaultForm.unit,
      quantity:    initial.quantity    ?? defaultForm.quantity,
      status:      initial.status      ?? defaultForm.status,
      category_id: initial.category_id ?? defaultForm.category_id,
      custom_sku:  initial.custom_sku  ?? defaultForm.custom_sku,
    } : {}),
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    media, urlError, setUrlError,
    removeMedia, moveMedia,
    handleFiles, addFromUrl,
    canAddMore, MAX_MEDIA, MAX_IMG_MB, MAX_VID_MB,
    isUploading,
  } = useMediaUpload(initial ? getProductMedia(initial as Product) : [])

  const set = (field: keyof FormData, value: string | number) => {
    setForm(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => { const e = { ...prev }; delete e[field]; return e })
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    const result = productSchema.safeParse(form)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      result.error.issues.forEach(e => {
        const field = e.path[0] as string
        if (field) fieldErrors[field] = e.message
      })
      setErrors(fieldErrors)
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return
    }
    setIsSubmitting(true)
    setErrors({})

    try {
      const finalData = { ...result.data as FormData }
      await onSubmit({
        ...finalData,
        media,
        images: media.map(m => m.src),
      })
      
    } catch (e: any) {
      setErrors({ submit: e.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmitRef = useRef(handleSubmit)
  useEffect(() => { handleSubmitRef.current = handleSubmit })
  
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { 
      if (e.key === 'Enter') handleSubmitRef.current() 
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div dir="rtl" className="bg-white rounded-[20px] shadow-[0_4px_24px_rgba(44,24,16,0.08)] p-7 flex flex-col gap-5">

      <FormFields
        form={form}
        errors={errors}
        set={set}
      />

      <div className="border-t border-cream-dark" />

      <MediaUploader
        media={media}
        canAddMore={canAddMore}
        MAX_MEDIA={MAX_MEDIA}
        MAX_IMG_MB={MAX_IMG_MB}
        MAX_VID_MB={MAX_VID_MB}
        urlError={urlError}
        setUrlError={setUrlError}
        onFiles={handleFiles}
        onRemove={removeMedia}
        onMove={moveMedia}
        onAddFromUrl={addFromUrl}
        isUploading={isUploading} 
      />

      <div className="border-t border-cream-dark" />

      <div className="flex gap-3 justify-end">
        <button
          type="button" onClick={onCancel}
          className="px-5 py-2.5 rounded-[10px] border border-cream-dark text-sm font-medium text-text-secondary bg-white hover:bg-cream transition-colors cursor-pointer"
        >
          ביטול
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`px-6 py-2.5 rounded-[10px] text-sm font-semibold text-white border-none
            shadow-[0_2px_8px_rgba(45,106,79,0.25)] transition-all
            ${isSubmitting 
              ? 'bg-green-primary/50 cursor-not-allowed' 
              : 'bg-green-primary cursor-pointer hover:shadow-[0_4px_12px_rgba(45,106,79,0.30)]'
            }`}
        >
          {isSubmitting ? 'שומר...' : submitLabel}
        </button>
      </div>
    </div>
  )
}